import type { KeyboardEvent } from 'react';
import { WEEKDAYS, longDay } from './UiDatePicker.constants';
import type { CalendarGridProps } from './UiDatePicker.types';
import {
    addDays,
    addMonths,
    chunk,
    clampToRange,
    fromIso,
    isInRange,
    monthGridDays,
    todayIso,
    weekdayIndex,
} from './UiDatePicker.utils';
import { UiDatePickerCell } from './UiDatePickerCell';

type UiDatePickerDaysProps = CalendarGridProps & {
    label: string;
    onSelect: (iso: string) => void;
};

/** The day a grid key moves the focus to; null for any other key. */
function targetDay(focus: string, event: KeyboardEvent): string | null {
    switch (event.key) {
        case 'ArrowLeft':
            return addDays(focus, -1);
        case 'ArrowRight':
            return addDays(focus, 1);
        case 'ArrowUp':
            return addDays(focus, -7);
        case 'ArrowDown':
            return addDays(focus, 7);
        case 'PageUp':
            return addMonths(focus, event.shiftKey ? -12 : -1);
        case 'PageDown':
            return addMonths(focus, event.shiftKey ? 12 : 1);
        case 'Home':
            return addDays(focus, -weekdayIndex(focus));
        case 'End':
            return addDays(focus, 6 - weekdayIndex(focus));
        default:
            return null;
    }
}

/** The month of the focused day in whole weeks, Monday first. */
export function UiDatePickerDays({ focus, value, range, label, onFocusChange, onSelect }: UiDatePickerDaysProps) {
    const view = fromIso(focus);
    const month = view.getMonth();
    const today = todayIso();
    const weeks = chunk(monthGridDays(view.getFullYear(), month), 7);

    const handleKeyDown = (event: KeyboardEvent) => {
        const target = targetDay(focus, event);
        if (!target) return;

        event.preventDefault();
        onFocusChange(clampToRange(target, range), true);
    };

    return (
        <div role="grid" aria-label={label} className="form-datepicker-grid" onKeyDown={handleKeyDown}>
            <div role="row" className="form-datepicker-row">
                {WEEKDAYS.map((weekday) => (
                    <span
                        key={weekday.long}
                        role="columnheader"
                        aria-label={weekday.long}
                        className="form-datepicker-weekday"
                    >
                        {weekday.short}
                    </span>
                ))}
            </div>

            {weeks.map((week) => (
                <div key={week[0]} role="row" className="form-datepicker-row">
                    {week.map((iso) => {
                        const date = fromIso(iso);

                        return (
                            <span key={iso} role="gridcell">
                                <UiDatePickerCell
                                    label={longDay(date)}
                                    focused={iso === focus}
                                    selected={iso === value}
                                    current={iso === today}
                                    disabled={!isInRange(iso, range)}
                                    outside={date.getMonth() !== month}
                                    onClick={() => onSelect(iso)}
                                >
                                    {date.getDate()}
                                </UiDatePickerCell>
                            </span>
                        );
                    })}
                </div>
            ))}
        </div>
    );
}
